import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Globe, User, Users, Hexagon } from 'lucide-react';
import { Card, CardCorner, StatBar, TranslatedText } from '../ui';

export function WhatWeDo() {
  const { t } = useTranslation();

  const features = [
    { 
      icon: Globe, 
      key: 'feature1',
      stats: [
        { label: t('whatWeDo.statReach'), value: 95 },
        { label: t('whatWeDo.statVisibility'), value: 88 },
      ]
    },
    { 
      icon: User, 
      key: 'feature2',
      stats: [
        { label: t('whatWeDo.statProfileScore'), value: 92 },
        { label: t('whatWeDo.statAnalytics'), value: 85 },
      ]
    },
    { 
      icon: Users, 
      key: 'feature3',
      stats: [
        { label: t('whatWeDo.statConnections'), value: 90 },
        { label: t('whatWeDo.statOpportunities'), value: 95 },
      ]
    },
  ];

  return (
    <section id="what-we-do" className="relative py-24 md:py-32 overflow-hidden bg-transparent">
      {/* Semi-transparent overlay for readability */}
      <div className="absolute inset-0 bg-brand-darkNavy/60 backdrop-blur-sm" />
      
      {/* Glow effects */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-yellow/5 rounded-full blur-[100px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          {/* FIFA-style section badge */}
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Hexagon className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">{t('whatWeDo.badge')}</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            <TranslatedText 
              i18nKey="whatWeDo.title" 
              type="digital"
              duration={0.8}
            />
          </h2>
          <p className="text-lg md:text-xl text-dark-300 leading-relaxed">
            <TranslatedText 
              i18nKey="whatWeDo.description" 
              type="fadeWords"
              duration={0.4}
              stagger={0.03}
            />
          </p>
          <p className="text-lg md:text-xl text-dark-300 leading-relaxed mt-4">
            <TranslatedText 
              i18nKey="whatWeDo.description2" 
              type="fadeWords"
              duration={0.4}
              stagger={0.03}
            />
          </p>
          <p className="text-xl md:text-2xl text-brand-yellow font-bold mt-6">
            <TranslatedText 
              i18nKey="whatWeDo.ready" 
              type="fadeLetters"
              duration={0.3}
              stagger={0.02}
            />
          </p>
          <motion.a
            href="#pricing"
            className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-brand-yellow text-brand-darkNavy font-bold text-lg rounded-full hover:bg-brand-yellow/90 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>⚽</span>
            <TranslatedText 
              i18nKey="whatWeDo.cta" 
              type="fade"
              duration={0.3}
            />
          </motion.a>
        </motion.div>

        {/* Features Grid - FIFA Player Card Style */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                variant="premium"
                padding="lg"
                hover
                className="group h-full"
                animate={false}
              >
                {/* Corner decorations */}
                <CardCorner position="top-left" />
                <CardCorner position="top-right" />
                <CardCorner position="bottom-left" />
                <CardCorner position="bottom-right" />
                
                {/* Icon with hexagon background */}
                <div className="relative mb-6">
                  <motion.div 
                    className="w-20 h-20 mx-auto relative"
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.6 }}
                  >
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <polygon 
                        points="50,5 93,25 93,75 50,95 7,75 7,25" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        className="text-brand-yellow/30"
                      />
                      <polygon 
                        points="50,15 83,30 83,70 50,85 17,70 17,30" 
                        fill="currentColor"
                        className="text-brand-yellow/10"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-brand-yellow" />
                    </div>
                  </motion.div>
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 text-center group-hover:text-brand-yellow transition-colors">
                  <TranslatedText 
                    i18nKey={`whatWeDo.${feature.key}Title`}
                    type="slideWords"
                    duration={0.4}
                    stagger={0.08}
                  />
                </h3>
                
                {/* Description */}
                <p className="text-dark-400 leading-relaxed text-center mb-6">
                  <TranslatedText 
                    i18nKey={`whatWeDo.${feature.key}Desc`}
                    type="fade"
                    duration={0.5}
                  />
                </p>
                
                {/* FIFA-style stat bars */}
                <div className="space-y-3 mt-auto">
                  {feature.stats.map((stat) => (
                    <StatBar key={stat.label} {...stat} />
                  ))}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
